import { RegionalPreview } from '@theme/studio-regional-preview';
import { ThemeEvents } from '@theme/events';

const GLOSSY_COLORS = new Set(['black', 'white', 'pink', 'light pink', 'clear', 'pearl white']);
const MATERIAL_BY_COLOR = new Map([
  ['black', 'black'],
  ['copper', 'metallic'],
  ['gold', 'metallic'],
  ['silver', 'metallic'],
  ['rock white', 'rock'],
  ['stone white', 'rock'],
  ['white', 'white'],
  ['pearl white', 'pearl'],
  ['clear', 'clear'],
]);

function getMaterial(colorName) {
  return MATERIAL_BY_COLOR.get(colorName.trim().toLowerCase()) || 'standard';
}

function getFinish(colorName) {
  return GLOSSY_COLORS.has(colorName.trim().toLowerCase()) ? 'glossy' : 'textured';
}

class LiveColorPreview extends HTMLElement {
  #controller = new AbortController();
  #regional;

  connectedCallback() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    if (this.dataset.regionalPreview) {
      this.#regional = new RegionalPreview(this, () => this.matchesSelection());
      this.#regional.connect();
      return;
    }
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelection, {
      signal: this.#controller.signal,
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#handleVariantSelection, {
        once: true,
        signal: this.#controller.signal,
      });
    }
    this.#syncFromPicker();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#regional?.disconnect();
  }

  #handleVariantSelection = () => {
    this.#syncFromPicker();
  };

  matchesSelection() {
    if (!this.dataset.geometrySelection || JSON.parse(this.dataset.geometrySelection).length === 0) return true;
    const picker = this.closest('.shopify-section')?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!picker) return false;
    const selections = Array.from(picker.querySelectorAll('input:checked, select option:checked'));
    return JSON.parse(this.dataset.geometrySelection).every(([name, value]) => selections.some(s => s.dataset.optionName === name && s.dataset.optionValue === value));
  }

  #syncFromPicker() {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!picker) return;
    this.style.visibility = this.matchesSelection() ? '' : 'hidden';

    const selections = Array.from(picker.querySelectorAll('input:checked, select option:checked'));
    const modelSelection = selections.find((selection) => selection.dataset.optionName === 'Model');
    const colorSelection = selections.find((selection) => selection.dataset.optionName === (this.dataset.colorOption || 'Color'));

    if (modelSelection?.dataset.optionValue) {
      this.dataset.model = modelSelection.dataset.optionValue;
      for (const preview of this.querySelectorAll('[data-preview-model]')) {
        preview.toggleAttribute('hidden', preview.dataset.previewModel !== this.dataset.model);
      }
    }

    const colorName = colorSelection?.dataset.optionValue || this.dataset.colorName || '';
    this.dataset.finish = getFinish(colorName);
    this.dataset.material = getMaterial(colorName);

    if (colorSelection?.dataset.previewColor && CSS.supports('color', colorSelection.dataset.previewColor)) {
      const color = colorSelection.dataset.previewColor;
      this.style.setProperty('--preview-color', color);
    }

    if (colorName) this.dataset.colorName = colorName;
    this.setAttribute('aria-label', `${this.dataset.model} in ${this.dataset.colorName}`);
  }
}

if (!customElements.get('live-color-preview')) {
  customElements.define('live-color-preview', LiveColorPreview);
}
